/** Pure Overview/ops display mapping — no I/O. D4A. */

import { cmsStrings } from "../terminology";

export type ReadinessStatus = "ok" | "degraded" | "fail";
export type StatusTone = "success" | "warning" | "danger" | "neutral" | "info";

export function readinessTone(status: ReadinessStatus | undefined | null): StatusTone {
  if (status === "ok") return "success";
  if (status === "degraded") return "warning";
  if (status === "fail") return "danger";
  return "neutral";
}

export function readinessLabelFa(status: ReadinessStatus | undefined | null): string {
  if (status === "ok") return "آماده";
  if (status === "degraded") return "کاهش‌یافته";
  if (status === "fail") return "ناموفق";
  return "نامشخص";
}

export function backupAgeTone(seconds: number | null | undefined): StatusTone {
  if (seconds == null) return "neutral";
  if (seconds > 36 * 3600) return "danger";
  if (seconds > 24 * 3600) return "warning";
  return "success";
}

export function formatBackupAgeFa(seconds: number | null | undefined): string {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds} ثانیه`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} دقیقه`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} ساعت`;
  return `${Math.floor(seconds / 86400)} روز`;
}

export function driftTone(count: number | null | undefined): StatusTone {
  if (count == null) return "neutral";
  if (count <= 0) return "success";
  if (count <= 2) return "warning";
  return "danger";
}

export function killSwitchTone(active: boolean | null | undefined): StatusTone {
  if (active == null) return "neutral";
  return active ? "danger" : "success";
}

export function killSwitchLabelFa(active: boolean | null | undefined): string {
  if (active == null) return "نامشخص";
  return active ? "فعال" : "خاموش";
}

const ALERT_LABELS: Record<string, string> = {
  public_content_5xx: "خطای ۵xx محتوای عمومی",
  cms_not_ready: "CMS آماده نیست",
  backup_stale: "پشتیبان قدیمی",
  storage_low: "فضای ذخیره‌سازی کم",
  bundle_fail_streak: "شکست پیاپی Bundle",
  kill_switch_on: cmsStrings.killSwitch.activeAlertLabel,
  restore_drill_overdue: "آزمایش بازگردانی عقب‌افتاده",
  ops_test: "هشدار آزمایشی عملیات",
};

export function alertLabelFa(alert: string): string {
  return ALERT_LABELS[alert] ?? alert;
}

export function alertSeverityTone(severity: string | undefined | null): StatusTone {
  const s = (severity ?? "").toLowerCase();
  if (s === "critical" || s === "error" || s === "danger") return "danger";
  if (s === "warning" || s === "warn") return "warning";
  if (s === "info") return "info";
  return "neutral";
}

export type AttentionItem = {
  id: string;
  tone: StatusTone;
  text: string;
  href?: string;
  linkLabel?: string;
};

export type OverviewAttentionCaps = {
  canReadEntitlements: boolean;
  canReadOps: boolean;
  canReadFleet: boolean;
  canEditContent: boolean;
  canPublish: boolean;
  canGenerateBundle: boolean;
  canSeePlatformAlerts: boolean;
};

const TONE_RANK: Record<StatusTone, number> = {
  danger: 0,
  warning: 1,
  info: 2,
  success: 3,
  neutral: 4,
};

export function buildOpsAttentions(input: {
  killActive?: boolean | null;
  readiness?: ReadinessStatus | null;
  backupAgeSeconds?: number | null;
  fleetDriftSites?: number | null;
  alertCount?: number | null;
} | null): AttentionItem[] {
  if (!input) return [];
  const items: AttentionItem[] = [];
  if (input.killActive) {
    items.push({
      id: "kill",
      tone: "danger",
      text: cmsStrings.killSwitch.activeAttention,
      href: "/settings",
      linkLabel: cmsStrings.nav.settings,
    });
  }
  // U0-011: readiness lives in HealthRow when canReadOps — do not repeat it in the strip.
  const backupTone = backupAgeTone(input.backupAgeSeconds);
  if (backupTone === "danger" || backupTone === "warning") {
    items.push({
      id: "backup",
      tone: backupTone,
      text: cmsStrings.overviewUi.attentionBackup,
      href: "/observability?tab=backup",
      linkLabel: cmsStrings.nav.observability,
    });
  }
  if ((input.fleetDriftSites ?? 0) > 0) {
    items.push({
      id: "drift",
      tone: driftTone(input.fleetDriftSites),
      text: cmsStrings.overviewUi.attentionDrift,
      href: "/fleet?tab=drift",
      linkLabel: cmsStrings.nav.drift,
    });
  }
  if ((input.alertCount ?? 0) > 0) {
    items.push({
      id: "alerts",
      tone: "warning",
      text: cmsStrings.overviewUi.attentionAlerts,
      href: "/observability?tab=alerts",
      linkLabel: cmsStrings.overviewUi.alertsAction,
    });
  }
  return items;
}

export function filterAttentionsForRole(
  items: AttentionItem[],
  caps: OverviewAttentionCaps,
): AttentionItem[] {
  return items.filter((item) => {
    if (item.id === "kill" || item.id === "backup") {
      return caps.canReadOps;
    }
    if (item.id === "drift") return caps.canReadFleet;
    if (item.id === "alerts") return caps.canSeePlatformAlerts;
    if (item.id === "drafts") return caps.canEditContent;
    if (item.id === "bundle-off") return caps.canGenerateBundle;
    if (item.id === "sponsored-off") return caps.canEditContent;
    if (item.id === "publish-off") return caps.canPublish || caps.canEditContent;
    if (item.id.endsWith("-quota")) return caps.canReadEntitlements;
    return true;
  });
}

export function buildOverviewAttentions(input: {
  entitlements: Parameters<typeof buildEntitlementAttentions>[0];
  ops: Parameters<typeof buildOpsAttentions>[0];
  draftCount: number;
  caps: OverviewAttentionCaps;
}): AttentionItem[] {
  const drafts: AttentionItem[] =
    input.draftCount > 0
      ? [
          {
            id: "drafts",
            tone: "warning",
            text: cmsStrings.overviewUi.attentionDrafts,
            href: "/announcements",
            linkLabel: cmsStrings.nav.announcements,
          },
        ]
      : [];
  const merged = [
    ...buildOpsAttentions(input.ops),
    ...buildEntitlementAttentions(input.entitlements),
    ...drafts,
  ];
  return filterAttentionsForRole(merged, input.caps)
    .sort((a, b) => TONE_RANK[a.tone] - TONE_RANK[b.tone] || a.id.localeCompare(b.id))
    .slice(0, 6);
}

export type OverviewHeaderAction = {
  to: string;
  label: string;
  kind: "primary" | "ghost";
};

export function buildOverviewHeaderActions(input: {
  canEditContent: boolean;
  canReadOps: boolean;
  canReadFleet: boolean;
  sponsoredAllowed: boolean;
}): OverviewHeaderAction[] {
  if (input.canEditContent) {
    const actions: OverviewHeaderAction[] = [
      {
        to: "/announcements",
        label: cmsStrings.overviewUi.createAnnouncement,
        kind: "primary",
      },
      {
        to: "/promotions",
        label: cmsStrings.overviewUi.createPromotion,
        kind: "ghost",
      },
    ];
    if (input.sponsoredAllowed) {
      actions.push({
        to: "/sponsored",
        label: cmsStrings.nav.sponsored,
        kind: "ghost",
      });
    }
    return actions;
  }
  if (input.canReadOps) {
    return [{ to: "/audit", label: cmsStrings.nav.audit, kind: "primary" }];
  }
  if (input.canReadFleet) {
    return [{ to: "/fleet", label: cmsStrings.nav.fleet, kind: "primary" }];
  }
  return [];
}

export type IntroChecklistStep = {
  id: string;
  text: string;
  href?: string;
  linkLabel?: string;
};

export function buildIntroChecklist(input: {
  canEditContent: boolean;
  canManageSites: boolean;
  canReadOps: boolean;
  canManageSettings: boolean;
}): IntroChecklistStep[] {
  const steps: IntroChecklistStep[] = [
    { id: "org", text: cmsStrings.overviewUi.introChecklistOrg },
  ];
  if (input.canEditContent) {
    steps.push({
      id: "content",
      text: cmsStrings.overviewUi.introChecklistContent,
      href: "/announcements",
      linkLabel: cmsStrings.nav.announcements,
    });
  }
  if (input.canManageSites) {
    steps.push({
      id: "sites",
      text: cmsStrings.overviewUi.introChecklistSites,
      href: "/sites",
      linkLabel: cmsStrings.nav.sitesAndGroups,
    });
  }
  if (input.canReadOps) {
    steps.push({
      id: "audit",
      text: cmsStrings.overviewUi.introChecklistAudit,
      href: "/audit",
      linkLabel: cmsStrings.nav.audit,
    });
  }
  if (input.canManageSettings) {
    steps.push({
      id: "settings",
      text: cmsStrings.overviewUi.introChecklistSettings,
      href: "/settings",
      linkLabel: cmsStrings.nav.settings,
    });
  }
  return steps.slice(0, 5);
}

export function introChecklistStorageKey(userId: string): string {
  return `vital-u3-intro-dismissed:${userId}`;
}

export function buildEntitlementAttentions(input: {
  usage: {
    activeSites: { warning: boolean };
    activeMembers: { warning: boolean };
    storageBytes: { warning: boolean };
  };
  features: { publish: boolean; bundleGenerate: boolean; sponsored: boolean };
} | null): AttentionItem[] {
  if (!input) return [];
  const items: AttentionItem[] = [];
  if (input.usage.activeSites.warning) {
    items.push({
      id: "sites-quota",
      tone: "warning",
      text: cmsStrings.overviewUi.attentionSitesQuota,
      href: "/entitlements",
    });
  }
  if (input.usage.activeMembers.warning) {
    items.push({
      id: "members-quota",
      tone: "warning",
      text: cmsStrings.overviewUi.attentionMembersQuota,
      href: "/entitlements",
    });
  }
  if (input.usage.storageBytes.warning) {
    items.push({
      id: "storage-quota",
      tone: "warning",
      text: cmsStrings.overviewUi.attentionStorageQuota,
      href: "/entitlements",
    });
  }
  if (!input.features.publish) {
    items.push({
      id: "publish-off",
      tone: "warning",
      text: cmsStrings.overviewUi.attentionPublishOff,
      href: "/entitlements",
    });
  }
  if (!input.features.bundleGenerate) {
    items.push({
      id: "bundle-off",
      tone: "warning",
      text: cmsStrings.overviewUi.attentionBundleOff,
      href: "/entitlements",
    });
  }
  if (!input.features.sponsored) {
    items.push({
      id: "sponsored-off",
      tone: "warning",
      text: cmsStrings.overviewUi.attentionSponsoredOff,
      href: "/entitlements",
    });
  }
  return items;
}
